import React, { useEffect, useState, MouseEvent } from "react";
import * as calendarTaskService from "source/models/services/calendarTaskService";
import { ISessionState } from "source/models/session/sessionState";
import { ICalendarTaskItem } from "ui/Scripts/source/models/calendarTask/calendarTaskItem";
import { CalendarTaskState, ConfirmationDialogResult, DatabaseRecordType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { getEventsByDate, getEventsByDay } from "../CalendarTaskList/ICalendarTaskListUtils";
import { AppUtils } from "source/framework/appUtils";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { ListIconButtonsItem } from "source/components/framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { CalendarTaskEditDialog } from "../CalendarTaskEditDialog";
import { Base } from "ui/Scripts/source/framework/base";
import { infiniteListDetailsViewPageSize, infiniteListPageSize } from "source/models/common/webConsts";
import { List } from "source/components/framework/List/List";
import { CalendarTaskListItem, ICalendarTaskDayItem } from "../CalendarTaskList/CalendarTaskListItem";
import { ToolButtonDropdownMenu } from "ui/Scripts/source/components/framework/Button/ToolButtonDropdownMenu";
import { removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { Alert, Box } from "@mui/material";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

interface ICalendarTaskListOfRecordProps {
    sessionState: ISessionState;
    databaseRecordType: DatabaseRecordType;
    isReadOnly: boolean;
    ownerRecordTitle: string;
    companyId: string;
    recordId: string;
    dataId: string;
    detailsViewMode?: boolean;
    noDataMessage?: string;
    infoText?: string;
    warningText?: string;
    onSetOpenCalendarTaskAmount?: (amount: number) => void;
    onIsLoadingChange?: (isLoading: boolean) => void;
}

interface ICalendarTaskListOfRecordListState {
    intialized: boolean;
    page: number;
    hasMore: boolean;
    stateFilter: CalendarTaskState[];
    items: ICalendarTaskItem[];
    groupedItems: ICalendarTaskDayItem[];
    selectedId: string;
    checkedIds: string[];

    showEditDialog: boolean;
    editDialogId: string;
    editDialogHasPrevious: boolean;
    editDialogHasNext: boolean;
}

export const getOpenCalendarTaskAmount = async (companyId: string, databaseRecordType: DatabaseRecordType, recordId: string) => {
    if (!recordId || recordId === Base.emptyGuid) return 0;
    const result = await AppUtils.callService(() => calendarTaskService.getCalendarTaskAmount(companyId, databaseRecordType, recordId, [CalendarTaskState.NotStarted, CalendarTaskState.Started]), false, true);
    if (!result) return 0;
    return result.amount;
};

export const CalendarTaskListOfRecord = (props: ICalendarTaskListOfRecordProps) => {
    const [isLoading, setIsLoading] = useState(false);
    
    useEffect(() => {
        if (!props.onIsLoadingChange) return;
        props.onIsLoadingChange(isLoading);
    }, [isLoading]);

    const [state, setState] = useState<ICalendarTaskListOfRecordListState>({
        intialized: false,
        page: 1,
        hasMore: true,
        stateFilter: [CalendarTaskState.NotStarted, CalendarTaskState.Started, CalendarTaskState.Finished],
        items: [],
        groupedItems: [],
        selectedId: "",
        checkedIds: [],
        showEditDialog: false,
        editDialogId: "",
        editDialogHasPrevious: false,
        editDialogHasNext: false
    });

    const setOpenCalendarTaskAmount = async () => {
        if (!props.onSetOpenCalendarTaskAmount) return;
        const amount = await getOpenCalendarTaskAmount(props.companyId, props.databaseRecordType, props.recordId);
        props.onSetOpenCalendarTaskAmount(amount);
    };

    const searchItems = async (page: number, resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setEditDialogData: boolean = true) => {
        const isNewRecord = props.recordId === Base.emptyGuid;
        if (isNewRecord) {
            setState((old) => ({ ...old, intialized: true }));
            return;
        }
        const pageSize = !props.detailsViewMode ? infiniteListPageSize : infiniteListDetailsViewPageSize;
        setIsLoading(true);
        const result = await AppUtils.callService(() => calendarTaskService.getCalendarTaskListItems(props.companyId, props.databaseRecordType, props.recordId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1), false, true, () => setIsLoading(false));
        if (!result) return;
        const items = List.getListItems(state.items, state.selectedId, state.checkedIds, result.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let selectedItem = index > -1 ? items.items[index] : null;
        if (moveNext && selectedItem && index < items.items.length - 1) {
            index = index + 1;
            selectedItem = items.items[index];
            selectedId = items.items[index].id;
        }
        const filteredItems = items.items.filter((item) => state.stateFilter.includes(item.state));
        const eventsByDay = getEventsByDay(filteredItems, null, null)
            .sort((a, b) => a.startDate - b.startDate);
        const groupedItems = getEventsByDate(
            eventsByDay
        );
        setState((old) => ({
            ...old,
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            items: items.items,
            groupedItems,
            selectedId,
            checkedIds: items.checkedIds,
            editDialogId: setEditDialogData ? (moveNext && selectedItem ? selectedItem.id : "") : state.editDialogId,
            editDialogHasPrevious: setEditDialogData ? index > 0 : state.editDialogHasPrevious,
            editDialogHasNext: setEditDialogData ? (index < items.items.length - 1 || result.hasMore) : state.editDialogHasNext,
        }));
        setOpenCalendarTaskAmount();
    };

    useEffect(() => {
        searchItems(1, true, false);
    }, [state.stateFilter, props.recordId]);

    // const handleFilterChange = (_event: React.MouseEvent<HTMLElement>, value: any) => {
    //     setStateFitler(value);
    // };

    const refreshList = async (setEditDialogData: boolean = true): Promise<void> => {
        await searchItems(state.page, false, true, false, setEditDialogData);
    };

    // #region Edit dialog
    const showEditDialog = (calendarTaskId: string, isSeries: boolean) => {
        const isNew = calendarTaskId === Base.emptyGuid;
        const index = isNew ? 0 : state.items.findIndex(i => i.id === calendarTaskId);
        if (!isSeries && index < 0) return;
        setState((old) => ({
            ...old,
            showEditDialog: true,
            editDialogId: calendarTaskId,
            editDialogHasPrevious: !isSeries && index > 0,
            editDialogHasNext: isSeries || isNew ? false : index < state.items.length - 1 || state.hasMore
        }));
    };

    const handleAddCalendarTask = () => {
        showEditDialog(Base.emptyGuid, false);
    };

    const handleOpenCalendarTask = (id: string, isSeries: boolean) => {
        showEditDialog(id, isSeries);
    };

    const handleOkEditDialog = (selectedId: string) => {
        refreshList(false);
    };

    const handleNewSavedEditDialog = (selectedId: string) => {
        setState((old) => ({
            ...old,
            selectedId,
            editDialogId: selectedId,
            editDialogHasPrevious: false,
            editDialogHasNext: false
        }));
        refreshList(false);
    };

    const handleCancelEditDialog = () => {
        setState((old) => ({
            ...old,
            showEditDialog: false,
        }));
        refreshList();
    };

    const setEditDialogItemByIndex = (index: number) => {
        if (index < 0) return;
        if (index >= state.items.length) {
            if (!state.hasMore) return;
            searchItems(state.page + 1, false, false, true);
        } else {
            setState((old) => ({
                ...old,
                selectedId: state.items[index].id,
                editDialogId: state.items[index].id,
                editDialogHasPrevious: index > 0,
                editDialogHasNext: index < state.items.length - 1 || state.hasMore,
            }));
        }
    };
    // #endregion Edit dialog

    //# region Remove
    const [removeMenuAnchorEl, setRemoveMenuAnchorEl] = useState<null | HTMLElement>(null);

    const remove = async (id: string, _isSeries: boolean) => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveCalendarTaskParameter, state.items?.find(item => item.id === id)?.title), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => calendarTaskService.removeCalendarTask(props.companyId, id));
            if (!result) return;
        }
        refreshList();
    };

    const getRemoveActions = () => {
        if (!state.selectedId) return [];
        const item = state.items.find(i => i.id === state.selectedId);
        if (!item) return [];
        return [
            { title: Translations.CalendarTaskRemoveOccurrence, onClick: () => { remove(item.id, false); } },
            { title: Translations.CalendarTaskRemoveSeries, onClick: () => { remove(item.parentId, true); } },
        ];
    };

    const handleRemove = async (event: MouseEvent<HTMLElement>) => {
        if (!state.selectedId) return;
        const item = state.items.find(i => i.id === state.selectedId);
        if (!item) return;
        if (item.parentId) {
            setRemoveMenuAnchorEl(event.currentTarget);
        } else {
            remove(item.id, false);
        }
    };
    //# endregion Remove
    
    const handleSetSelectedId = (selectedId: string) => {
        setState((old) => ({
            ...old,
            selectedId
        }));
    };

    const handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        searchItems(!nextPage ? 1 : state.page + 1, !nextPage, false);
    };

    return (<>
        <VerticalContainer
            before={<>
                <Box>
                    {props.infoText &&
                        <Alert sx={{ whiteSpace: "pre-line" }} severity="info">{props.infoText}</Alert>
                    }
                    {props.warningText &&
                        <Alert sx={{ whiteSpace: "pre-line", marginTop: props.infoText ? ".5rem" : 0 }} severity="warning">{props.warningText}</Alert>
                    }
                </Box>
                <ActionContainer
                    margin="0 0 1rem 0"
                    rightItems={
                        <>
                            <ListIconButtonsItem
                                addTooltip={Translations.AddCalendarTask}
                                removeTooltip={Translations.RemoveCalendarTask}
                                addDisabled={props.isReadOnly}
                                removeDisabled={props.isReadOnly || !state.selectedId && state.checkedIds.length < 1}
                                onAdd={handleAddCalendarTask}
                                onRemove={handleRemove}
                            />
                            <ToolButtonDropdownMenu
                                anchorEl={removeMenuAnchorEl}
                                actions={getRemoveActions()}
                                onClose={() => setRemoveMenuAnchorEl(null)}
                            />
                        </>
                    }
                />
            </>}
            main={<>
                <List
                    intialized={state.intialized}
                    isLoading={!props.onIsLoadingChange && isLoading}
                    localLoadingDisabled={!!props.onIsLoadingChange}
                    globalLoadingInUse={false}
                    noDataMessage={props.recordId === Base.emptyGuid ? props.noDataMessage : null}
                    hasMore={!props.detailsViewMode && state.hasMore}
                    height={!props.detailsViewMode
                        ? "100%"
                        : "auto"}
                    customLines={state.groupedItems.map((item) => (
                        <CalendarTaskListItem
                            key={item.id}
                            item={item}
                            selectedId={state.selectedId}
                            onOpen={handleOpenCalendarTask}
                            onClick={handleSetSelectedId}
                        />
                    ))}
                    sortColumn={""}
                    sortOrderIsAsc={true}
                    selectedId={state.selectedId}
                    checkedIds={state.checkedIds}
                    onSetSelectedIds={null}
                    onSearchLines={handleSearchLines}
                />
            </>}
            disableMainInitialHeight={props.detailsViewMode}
        />
        {state.showEditDialog &&
            <CalendarTaskEditDialog
                isReadOnly={props.isReadOnly}
                companyId={props.companyId}
                calendarTaskId={state.editDialogId}
                recordId={props.recordId}
                databaseRecordType={props.databaseRecordType}
                startDate={null}
                sessionState={props.sessionState}
                hasNext={state.editDialogHasNext}
                hasPrevious={state.editDialogHasPrevious}
                ownerRecordTitle={props.ownerRecordTitle}
                onRefreshCalendar={() => refreshList(false)}
                onPrevious={() => List.moveToPrevious(state, setEditDialogItemByIndex)}
                onNext={() => List.moveToNext(state, setEditDialogItemByIndex)}
                onSaved={handleOkEditDialog}
                onClose={handleCancelEditDialog}
                onNewSaved={handleNewSavedEditDialog}
            />
        }
    </>);
};
